import { useCallback, useEffect, useState } from 'react';

const SESSION_KEY = 'bxi_demo_prompt_shown';
// Give the visitor a short while to look around, then surface the prompt.
const REVEAL_DELAY_MS = 10000;

/**
 * Drives the "Book a demo" experience on the landing pages.
 *
 * - Only auto-opens when `enabled` is true. On the landing site every visitor is
 *   a guest (logged-in users are redirected to the dashboard), so this is true.
 * - Auto-opens once per browser session, purely on a timer (REVEAL_DELAY_MS)
 *   after the page loads — no scrolling required.
 * - After it has been shown once, `showBar` stays true whenever the popup is
 *   closed, so the caller can render a floating top bar to re-open it.
 * - `openPrompt`/`closePrompt` let callers open/close it manually.
 */
const useDemoBookingPrompt = ({ enabled = true } = {}) => {
  const [open, setOpen] = useState(false);
  const [hasOpenedOnce, setHasOpenedOnce] = useState(false);
  window.lintrk('track', { conversion_id: 29964713 });


  useEffect(() => {
    if (!enabled) return undefined;

    // Already shown earlier this session: keep the bar available, don't re-pop.
    if (sessionStorage.getItem(SESSION_KEY)) {
      setHasOpenedOnce(true);
      return undefined;
    }

    const timer = setTimeout(() => {
      sessionStorage.setItem(SESSION_KEY, '1');
      setHasOpenedOnce(true);
      setOpen(true);
    }, REVEAL_DELAY_MS);

    return () => clearTimeout(timer);
  }, [enabled]);

  // Manual open always works (used by the floating top bar).
  const openPrompt = useCallback(() => {
    sessionStorage.setItem(SESSION_KEY, '1');
    setHasOpenedOnce(true);
    setOpen(true);
  }, []);

  const closePrompt = useCallback(() => setOpen(false), []);

  // Show the re-open bar once the popup has been shown and is now closed.
  const showBar = enabled && hasOpenedOnce && !open;

  return { open, openPrompt, closePrompt, showBar, setOpen };
};

export default useDemoBookingPrompt;
